import { ToastAction, useToast } from '@folio/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AttachEmailThreadsPoliciesBody,
  DetachEmailsPoliciesBody,
  EmailThreadDto,
} from 'korint-sdk';
import {
  emailControllerDetachPolicies,
  emailThreadControllerAttachThreadsPolicies,
  emailThreadControllerGetThreadQueryKey,
  emailThreadControllerListThreadsQueryKey,
  generalInboxControllerGetSummaryQueryKey,
} from 'korint-sdk';
import { chunk, uniq, uniqBy } from 'lodash';
import { Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MultiplePolicyPicker } from './PolicyPicker';
import { participantAddresses } from './threadHelpers';
import { usePolicyLabel } from './ThreadIndicators';

const SEARCHED_ADDRESS_COUNT = 3;
// Detach endpoint cap; a batch of threads can attach more emails
const MAX_DETACHED_EMAILS = 1000;
// Attach endpoint cap
const MAX_ATTACHED_POLICIES = 10;

export function AttachSelectionButton({
  threads,
  disabled,
  onSubmit,
}: {
  threads: EmailThreadDto[];
  disabled: boolean;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { label } = usePolicyLabel();
  const queryClient = useQueryClient();
  const refresh = (threadIds: string[]) => {
    queryClient.invalidateQueries({ queryKey: emailThreadControllerListThreadsQueryKey() });
    queryClient.invalidateQueries({ queryKey: generalInboxControllerGetSummaryQueryKey() });
    for (const threadId of threadIds) {
      queryClient.invalidateQueries({ queryKey: emailThreadControllerGetThreadQueryKey(threadId) });
    }
  };

  const { mutateAsync: attach } = useMutation({
    mutationFn: (body: AttachEmailThreadsPoliciesBody) =>
      emailThreadControllerAttachThreadsPolicies(body),
    onSettled: (_, __, { threadIds }) => refresh(threadIds),
  });
  const { mutateAsync: detach } = useMutation({
    mutationFn: ({ threadIds: _, ...body }: DetachEmailsPoliciesBody & { threadIds: string[] }) =>
      emailControllerDetachPolicies(body),
    onSettled: (_, __, { threadIds }) => refresh(threadIds),
  });

  const threadIds = threads.map(({ id }) => id);
  return (
    <MultiplePolicyPicker
      label={t('generalInbox.selection.attach')}
      icon={Link2}
      suggestedPolicies={uniqBy(
        threads.flatMap(({ linkedPolicies }) => linkedPolicies),
        ({ id }) => id,
      )}
      participantAddresses={uniq(
        threads.flatMap(thread =>
          participantAddresses([thread], thread.account.primaryEmailAddress),
        ),
      ).slice(0, SEARCHED_ADDRESS_COUNT)}
      onSubmit={policies => {
        const policyIds = policies.map(({ id }) => id);
        attach({ threadIds, policyIds }).then(
          ({ emailIds }) => {
            onSubmit();
            if (emailIds.length === 0) return;
            toast({
              description: t('generalInbox.selection.attached', {
                count: emailIds.length,
                policy: policies.map(label).join(', '),
              }),
              action: (
                <ToastAction
                  altText={t('generalInbox.archive.undo')}
                  onClick={() =>
                    Promise.all(
                      chunk(emailIds, MAX_DETACHED_EMAILS).map(emailIdsChunk =>
                        detach({ emailIds: emailIdsChunk, policyIds, threadIds }),
                      ),
                    ).catch(() => {})
                  }
                >
                  {t('generalInbox.archive.undo')}
                </ToastAction>
              ),
            });
          },
          () => {},
        );
      }}
      className="size-7 opacity-100"
      testId="general-inbox-attach-selection"
      disabled={disabled}
      maxSelected={MAX_ATTACHED_POLICIES}
    />
  );
}
